import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Server, Activity, Database, Wifi, RefreshCw, Clock,
  CheckCircle2, AlertTriangle, XCircle, Cpu, Globe, Zap, HardDrive,
  Play, Pause
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

const STATUS_CONFIG = {
  operational: { color: 'text-emerald-400', bg: 'bg-emerald-400', icon: CheckCircle2, labelKey: 'infra.operational' },
  degraded: { color: 'text-amber-400', bg: 'bg-amber-400', icon: AlertTriangle, labelKey: 'infra.degraded' },
  down: { color: 'text-red-400', bg: 'bg-red-400', icon: XCircle, labelKey: 'infra.offline' },
  running: { color: 'text-emerald-400', bg: 'bg-emerald-400', icon: CheckCircle2, labelKey: 'infra.running' },
  stopped: { color: 'text-red-400', bg: 'bg-red-400', icon: XCircle, labelKey: 'infra.stopped' },
  unknown: { color: 'text-slate-400', bg: 'bg-slate-400', icon: AlertTriangle, labelKey: 'infra.unknown' },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-1.5 h-1.5 rounded-full ${cfg.bg} animate-pulse`} />
      <span className={`text-[10px] font-mono font-bold uppercase tracking-wider ${cfg.color}`}>{t(cfg.labelKey)}</span>
    </div>
  );
}

function LatencyBar({ value, max = 2000 }) {
  const pct = Math.min((value / max) * 100, 100);
  const color = value < 500 ? 'bg-emerald-500' : value < 1500 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#11141D] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-mono ${value < 500 ? 'text-emerald-400' : value < 1500 ? 'text-amber-400' : 'text-red-400'}`}>
        {value}ms
      </span>
    </div>
  );
}

const PROVIDER_COLORS = {
  'CoinGecko': '#f59e0b',
  'yFinance (Equities)': '#10b981',
  'yFinance (ETFs)': '#3b82f6',
  'yFinance (Forex)': '#8b5cf6',
};

const chartTooltipStyle = {
  background: '#11141D', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 0, fontFamily: 'monospace', fontSize: 11
};

export default function InfraDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/infra/dashboard');
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching infra:', err);
    }
    setLoading(false);
    setCountdown(30);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, 30000);
      countdownRef.current = setInterval(() => {
        setCountdown(prev => (prev <= 1 ? 30 : prev - 1));
      }, 1000);
    }
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [autoRefresh, fetchData]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center" data-testid="infra-dashboard-page">
        <div className="text-center">
          <Server className="w-10 h-10 text-cyan-400 animate-pulse mx-auto mb-3" />
          <p className="text-slate-400 font-mono text-sm">{t('infra.loading')}</p>
        </div>
      </div>
    );
  }

  const providers = data?.providers || [];
  const engine = data?.engine || {};
  const database = data?.database || {};
  const cacheStats = data?.cache_stats || [];
  const providerInfo = data?.provider_info || {};
  const latencyTimeline = data?.latency_timeline || [];
  const healthScore = data?.health_score ?? 0;

  return (
    <div data-testid="infra-dashboard-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08] px-4 md:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Server className="w-5 h-5 text-cyan-400" />
            <h1 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('infra.title')}</h1>
            <span className="text-[10px] font-mono text-slate-500 bg-white/5 px-2 py-0.5">{t('infra.subtitle')}</span>
          </div>
          <div className="flex items-center gap-3">
            {autoRefresh && (
              <div className="flex items-center gap-1.5">
                <div className="relative w-5 h-5">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#06b6d4" strokeWidth="3"
                      strokeDasharray={`${(countdown / 30) * 88} 88`} strokeLinecap="round"
                      className="transition-all duration-1000 ease-linear" />
                  </svg>
                </div>
                <span className="text-[10px] font-mono text-cyan-400 w-4 tabular-nums">{countdown}</span>
              </div>
            )}
            <button onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono font-bold tracking-wider transition-all ${
                autoRefresh
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/5 text-slate-500 border border-white/10 hover:text-white hover:border-white/20'
              }`}
              data-testid="auto-refresh-toggle">
              {autoRefresh ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {autoRefresh ? t('infra.live') : t('infra.auto')}
            </button>
            {lastRefresh && (
              <span className="text-[10px] font-mono text-slate-600">
                {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button onClick={fetchData} className="text-slate-400 hover:text-white transition-colors" data-testid="infra-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <main className="p-4 md:p-6 space-y-6">
        {/* Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="infra-overview">
          <div className="bg-[#090B0F] border border-white/10 p-5 md:col-span-1">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-3">{t('infra.healthScore')}</div>
            <div className="relative w-24 h-24 mx-auto">
              <svg viewBox="0 0 36 36" className="w-full h-full">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="2.5" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke={healthScore >= 75 ? '#10b981' : healthScore >= 50 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="2.5" strokeDasharray={`${healthScore}, 100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-2xl font-black font-mono ${healthScore >= 75 ? 'text-emerald-400' : healthScore >= 50 ? 'text-amber-400' : 'text-red-400'}`}
                  data-testid="health-score">{healthScore}%</span>
              </div>
            </div>
          </div>

          <div className="bg-[#090B0F] border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t('infra.uptime')}</span>
            </div>
            <p className="text-2xl font-black font-mono text-white" data-testid="uptime">{data?.uptime_hours || 0}h</p>
            <p className="text-[10px] text-slate-500 mt-1">{t('infra.sinceLastRestart')}</p>
          </div>

          <div className="bg-[#090B0F] border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t('infra.engine')}</span>
            </div>
            <StatusBadge status={engine.status} />
            <p className="text-lg font-black font-mono text-white mt-2" data-testid="engine-cycles">{engine.cycle_count} {t('infra.cycles')}</p>
            <p className="text-[10px] text-slate-500 mt-1">{engine.active_agents}/{engine.total_agents} {t('infra.activeAgents')}</p>
          </div>

          <div className="bg-[#090B0F] border border-white/10 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{t('infra.database')}</span>
            </div>
            <StatusBadge status={database.status} />
            <p className="text-lg font-black font-mono text-white mt-2" data-testid="db-latency">{database.latency_ms}ms</p>
            <p className="text-[10px] text-slate-500 mt-1">{database.collections} {t('infra.collections')}</p>
          </div>
        </div>

        {/* Provider Latency */}
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="provider-latency-section">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
              <Globe className="w-4 h-4 text-cyan-400" /> {t('infra.providerLatency')}
            </h3>
          </div>
          <div className="divide-y divide-white/5">
            {providers.map(p => (
              <div key={p.provider} className="px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`provider-${p.provider.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[p.provider] || '#64748b' }} />
                    <span className="text-sm font-medium text-white">{p.provider}</span>
                    <StatusBadge status={p.status} />
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">{p.data_points} {t('infra.dataPoints')}</span>
                </div>
                <LatencyBar value={p.latency_ms} />
                {p.error && (
                  <p className="text-[10px] text-red-400 mt-1 font-mono">{p.error}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Latency Timeline Chart */}
        {latencyTimeline.length > 0 && (
          <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="latency-timeline-chart">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" /> {t('infra.realtimeLatency')}
            </h3>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="time" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false}
                    label={{ value: 'ms', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 9 }} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [`${v}ms`, '']} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'monospace' }} />
                  {providers.map(p => (
                    <Line key={p.provider} type="monotone" dataKey={p.provider}
                      stroke={PROVIDER_COLORS[p.provider] || '#64748b'}
                      strokeWidth={1.5} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Cache Freshness + Provider Config */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Cache Status */}
          <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="cache-status-section">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-slate-400" /> {t('infra.dataCache')}
              </h3>
            </div>
            {cacheStats.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-xs font-mono">{t('infra.cacheEmpty')}</div>
            ) : (
              <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
                {cacheStats.map(c => (
                  <div key={c.key} className="flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02]">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${c.fresh ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      <span className="text-xs font-mono text-white">{c.key}</span>
                    </div>
                    <span className={`text-[10px] font-mono ${c.fresh ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {c.age_seconds < 60 ? `${c.age_seconds.toFixed(0)}s` : `${(c.age_seconds / 60).toFixed(1)}m`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Provider Config */}
          <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="provider-config-section">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                <Wifi className="w-4 h-4 text-emerald-500" /> {t('infra.providerConfig')}
              </h3>
            </div>
            <div className="divide-y divide-white/5">
              {Object.entries(providerInfo).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02]">
                  <span className="text-xs text-slate-400">{key}</span>
                  <span className="text-xs font-mono text-white">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                </div>
              ))}
              {Object.keys(providerInfo).length === 0 && (
                <div className="p-6 text-center text-slate-500 text-xs font-mono">{t('infra.noProviderInfo')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Engine Details */}
        <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="engine-details-section">
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" /> {t('infra.engineDetails')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#11141D] border border-white/5 p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1">{t('infra.totalCycles')}</div>
              <div className="text-lg font-black font-mono text-white">{engine.cycle_count}</div>
            </div>
            <div className="bg-[#11141D] border border-white/5 p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1">{t('infra.openPositions')}</div>
              <div className="text-lg font-black font-mono text-white">{engine.positions_open}</div>
            </div>
            <div className="bg-[#11141D] border border-white/5 p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1">{t('infra.totalPnl')}</div>
              <div className={`text-lg font-black font-mono ${engine.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                ${engine.total_pnl?.toLocaleString()}
              </div>
            </div>
            <div className="bg-[#11141D] border border-white/5 p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1">{t('infra.activeAgentsCount')}</div>
              <div className="text-lg font-black font-mono text-emerald-400">{engine.active_agents}/{engine.total_agents}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
